import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { filter, pipe, switchMap, tap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { HttpErrorResponse } from '@angular/common/http';
import { IPoint } from '@foblex/2d';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { SelectedEntityState } from '../../../shared/store/selected-entity.feature';
import { DialogConflitÉtapeComponent } from '../../components/import-etape/dialog-conflit-étape/dialog-conflit-étape.component';
import { Étape } from '../../models/étape.model';
import { EtapeService } from '../parcours-etape.service';
import { EtapeCreateRequest } from '../requests/create-request';
import { EtapeUpdateRequest } from '../requests/update-request';

export function avecMéthodesEtapes<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Étape> & SelectedEntityState>(),
      props: type<EntityProps<Étape>>(),
    },
    withMethods((store) => ({
      chargerEtapes: (state: { etapes: Étape[] }) => patchState(store, setAllEntities(state.etapes)),
      modifierEtape: (state: { id: string; etape: Partial<Étape> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.etape })),
      ajouterEtape: (state: { etape: Étape }) => patchState(store, upsertEntity(state.etape)),
      supprimerEtape: (state: { id: string }) => patchState(store, removeEntity(state.id)),
      selectionnerEtape: (state: { id: string }) => patchState(store, { selectedEntityId: state.id }),
    })),
    withMethods(
      (
        store,
        service = inject(EtapeService),
        sidePanelService = inject(SidepanelService),
        dialogService = inject(HlmDialogService),
      ) => ({
        charger: rxMethod<string>(
          pipe(
            switchMap((idParcours) =>
              service.trouverTous(idParcours).pipe(
                tapResponse({
                  next: (result) => store.chargerEtapes({ etapes: result.items }),
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la récupération des étapes, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        créer: rxMethod<{ request: EtapeCreateRequest; next: (idNouvelleÉtape: string) => void }>(
          pipe(
            switchMap(({ request, next }) =>
              service.créer(request).pipe(
                tapResponse({
                  next: (result) => {
                    store.ajouterEtape({ etape: result });
                    next(result.id);
                  },
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la modification d'une étape, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        modifier: rxMethod<{
          request: EtapeUpdateRequest;
          next?: () => void;
        }>(
          pipe(
            switchMap(({ request, next }) =>
              service.modifier(request).pipe(
                tapResponse({
                  next: () => {
                    request.etapes.forEach((etape) => {
                      store.modifierEtape({ id: etape.id, etape });
                    });
                    next?.();
                  },
                  error: (err) => {
                    sidePanelService.loading.set(false);
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la modification d'une étape, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        toggleSuppression: rxMethod<{ id: string; next: () => void }>(
          pipe(
            tap(({ id }) => store.supprimerEtape({ id })),
            switchMap(({ id, next }) =>
              service.supprimer(id).pipe(
                tapResponse({
                  next: () => next(),
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la suppression de l'étape, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        selection: rxMethod<string>(
          pipe(
            switchMap((id) => service.trouveParId(id)),
            tapResponse({
              next: (etape) => {
                store.ajouterEtape({ etape });
                store.selectionnerEtape({ id: etape.id });
              },
              error: (err) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message: `Un problème est survenu lors de la récupération de l'étape, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
        getEtape: rxMethod<string>(
          pipe(
            switchMap((id) => service.trouveParId(id)),
            tapResponse({
              next: (etape) => {
                store.modifierEtape({
                  id: etape.id,
                  etape,
                });
              },
              error: (err) => {
                sidePanelService.loading.set(false);
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message: `Un problème est survenu lors de la récupération de l'étape, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
        importerEtape: rxMethod<CopyPayload>(
          pipe(
            switchMap(({ étapeSource, idParcours, coords, next }) =>
              service.dupliquer(étapeSource.id, idParcours, coords).pipe(
                tapResponse({
                  next: (etape) => {
                    store.ajouterEtape({ etape });
                    next();
                  },
                  error: (err: HttpErrorResponse) => {
                    sidePanelService.loading.set(false);
                    console.error(err);
                    if (err.status === 409) {
                      const dialogRef = dialogService.open(DialogConflitÉtapeComponent, {
                        context: { étape: étapeSource },
                      });

                      dialogRef.closed$
                        .pipe(
                          filter(Boolean),
                          switchMap(() =>
                            service.dupliquer(étapeSource.id, idParcours, coords, true).pipe(
                              tapResponse({
                                next: (etape) => {
                                  store.modifierEtape({ id: etape.id, etape });
                                  next();
                                },
                                error: (err: HttpErrorResponse) => {
                                  sidePanelService.loading.set(false);
                                  console.error(err);
                                  const toastrId = uuidv7();
                                  toast.custom(ToastrErrorComponent, {
                                    componentProps: {
                                      message: `Un problème est survenu lors de la récupération de l'étape, contactez le support.`,
                                      id: toastrId,
                                    },
                                    id: toastrId,
                                  });
                                },
                              }),
                            ),
                          ),
                        )
                        .subscribe();

                      return;
                    }
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la récupération de l'étape, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        download: rxMethod<{ id: string; next: (blob: Blob) => void }>(
          pipe(
            switchMap(({ id, next }) => {
              return service.downloadParId(id).pipe(
                tapResponse({
                  next: (blob: Blob) => next(blob),
                  error: (err) => {
                    sidePanelService.loading.set(false);
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la récupération de l'étape, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              );
            }),
          ),
        ),
      }),
    ),
  );
}

export interface CopyPayload {
  étapeSource: Étape;
  idParcours: string;
  coords: IPoint;
  next: () => void;
}
